import React, { useContext, useMemo } from 'react';
import { RICH_TEXT_TYPES } from '../../utils/richTextUtils';
import useCustomContentBlockComponents from '../../hooks/useCustomContentBlockComponents';
import useRichTextBuilder from '../../hooks/useRichTextBuilder';
import { KovContentBlockContext } from './KovContentBlockProvider';

export const KovContentBlock = ({
  item = {},
  itemList,
  showCollapsed = false,
  titleLevel = 1,
  displayRootItem = true,
}) => {
  const { baseUrl, compilerOptions, color, customComponents, customFuncs, allTitlesH1 } =
    useContext(KovContentBlockContext);
  const children = useRichTextBuilder({ item, itemList });
  const {
    Title,
    Collapsed,
    Paragraph,
    Image,
    ImageGroup,
    Summary,
    Legal,
    Quote,
    Video,
    ReferenceGroup,
    Attachments,
    Example,
  } = useCustomContentBlockComponents(customComponents);
  const contentBlockChildren = useMemo(() => {
    let nextTitleLevel = titleLevel;
    if (item.type === RICH_TEXT_TYPES.SECTION && !allTitlesH1) nextTitleLevel += 1;

    return children?.map((child, key) => (
      <KovContentBlock
        key={key}
        item={child}
        itemList={itemList}
        showCollapsed={showCollapsed}
        titleLevel={nextTitleLevel}
      />
    ));
  }, [item, children, itemList, showCollapsed, titleLevel, allTitlesH1]);

  const shouldRenderChildren = customFuncs?.showCollapsed
    ? !customFuncs?.showCollapsed(item)
    : !showCollapsed &&
      item.type !== RICH_TEXT_TYPES.PARAGRAPH &&
      item.type !== RICH_TEXT_TYPES.IMAGE_GROUP &&
      item.type !== RICH_TEXT_TYPES.EXAMPLE;

  if (displayRootItem === false) {
    return <div id={item.key}>{shouldRenderChildren && contentBlockChildren}</div>;
  }

  return (
    <div id={item.key}>
      {item.type === RICH_TEXT_TYPES.IMAGE && item.parent?.type !== RICH_TEXT_TYPES.IMAGE_GROUP && (
        <Image item={item} baseUrl={baseUrl} />
      )}
      {item.type === RICH_TEXT_TYPES.IMAGE_GROUP && <ImageGroup item={item} baseUrl={baseUrl} />}

      {item.type === RICH_TEXT_TYPES.SUMMARY && <Summary item={item} />}
      {item.type === RICH_TEXT_TYPES.VIDEO && <Video item={item} />}
      {item.type === RICH_TEXT_TYPES.QUOTE && <Quote item={item} color={color} />}
      {item.type === RICH_TEXT_TYPES.LEGAL && <Legal item={item}>{contentBlockChildren}</Legal>}

      {(item.type === RICH_TEXT_TYPES.PARAGRAPH || item.type === RICH_TEXT_TYPES.TERM) && (
        <Paragraph item={item} baseUrl={baseUrl} compilerOptions={compilerOptions}>
          {contentBlockChildren}
        </Paragraph>
      )}

      {item.type === RICH_TEXT_TYPES.SECTION && !showCollapsed && (
        <Title
          item={item}
          color={color}
          compilerOptions={compilerOptions}
          titleLevel={titleLevel}
        ></Title>
      )}
      {item.type === RICH_TEXT_TYPES.SECTION && showCollapsed && (
        <Collapsed item={item} color={color}>
          {contentBlockChildren}
        </Collapsed>
      )}

      {item.type === RICH_TEXT_TYPES.ATTACHMENTS_GROUP && <Attachments item={item} />}
      {item.type === RICH_TEXT_TYPES.REFERENCE_GROUP && (
        <ReferenceGroup item={item} color={color} />
      )}
      {item.type === RICH_TEXT_TYPES.EXAMPLE && <Example item={item} color={color} />}

      {shouldRenderChildren && contentBlockChildren}
    </div>
  );
};

export default KovContentBlock;
